// src/components/common/UserMenu.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { UserProfileHeader, UserMenuItems, showSuccessToast } from '@/components/common';
import { UserSelectionDialog } from '@/components/dialogs/UserSelectionDialog';
import {
    Avatar, AvatarFallback, AvatarImage, Button, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui";
import { authService } from '@/services/authService';
import type { RootState } from '@/store';
import { logout } from '@/store/slices/authSlice';
import type { User } from '@/types/user';
import { logError } from '@/utils/logger';

export default function UserMenu() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const [userSelectionOpen, setUserSelectionOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await authService.logout();
            dispatch(logout());
            navigate('/auth/login');
        } catch (error) {
            logError("Logout failed", error instanceof Error ? error : new Error(String(error)));
        }
    };

    const handleUserSelect = (selectedUser: User) => {
        // TODO: Implement user switching logic
        console.log('Selected user:', selectedUser);
        //navigate(`/auth/login?user=${selectedUser.id}`);
        showSuccessToast(t("header.userSwitched", { user: selectedUser.name }));
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 p-0 overflow-hidden">
                        <Avatar>
                            <AvatarImage src={user?.avatar || "/placeholder-avatar.png"} alt={t("header.noUserImage")} />
                            <AvatarFallback>{user?.username?.slice(0, 2).toUpperCase() || "PS"}</AvatarFallback>
                        </Avatar>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                    <UserProfileHeader
                        name={user?.username || t("header.guest")}
                        role={user?.role || t("header.guestRole")}
                    />
                    <UserMenuItems
                        onLogout={handleLogout}
                        onLoginAsClient={() => setUserSelectionOpen(true)}
                    />
                </DropdownMenuContent>
            </DropdownMenu>

            <UserSelectionDialog
                open={userSelectionOpen}
                onOpenChange={setUserSelectionOpen}
                onUserSelect={handleUserSelect}
            />
        </>
    );
} 